import React, { useState, useEffect, useCallback } from 'react';
import { Tag, User, Home, Plus, Edit2, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { billCategoriesAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { ConfirmModal } from '../modals';
import BaseModal from '../common/BaseModal';
import { FormGroup } from '../common/FormComponents';
import { TextInput } from '../common';
import FilterDropdown from '../common/FilterDropdown';
import OwnershipBadge from '../common/OwnershipBadge';

// Predefined emojis for categories
const CATEGORY_EMOJIS = [
  { value: '⚡', label: 'Utilities' },
  { value: '🏠', label: 'Housing' },
  { value: '🛡️', label: 'Insurance' },
  { value: '📱', label: 'Phone/Tech' },
  { value: '💳', label: 'Finance' },
  { value: '📋', label: 'General' },
  { value: '🚗', label: 'Auto' },
  { value: '🏥', label: 'Health' },
  { value: '🎬', label: 'Entertainment' },
  { value: '🛒', label: 'Shopping' },
  { value: '📺', label: 'Streaming' },
  { value: '🌐', label: 'Internet' },
  { value: '💧', label: 'Water' },
  { value: '🔥', label: 'Gas/Heating' },
  { value: '🎓', label: 'Education' },
  { value: '🏋️', label: 'Fitness' },
  { value: '🐕', label: 'Pets' },
  { value: '👶', label: 'Childcare' },
  { value: '✈️', label: 'Travel' },
  { value: '🎁', label: 'Subscriptions' },
];

/**
 * BillCategories - Bill categories management component
 */
function BillCategories({ isManager, isPrivateMode = false, isInHousehold: isInHouseholdProp }) {
  const { success, error: showError } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [categoryTypeFilter, setCategoryTypeFilter] = useState('all');
  const [defaultFilter, setDefaultFilter] = useState('all');

  const canEdit = isManager || isPrivateMode;
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  // Use prop if provided, otherwise fall back to localStorage check
  const isInHousehold = isInHouseholdProp !== undefined ? isInHouseholdProp : !!currentUser.household_id;

  // Filter categories
  const filteredCategories = categories.filter(category => {
    // Ownership filter
    if (categoryTypeFilter === 'personal') {
      if (category.household_id && !category.is_default) return false;
    } else if (categoryTypeFilter === 'household') {
      if (!category.household_id && !category.is_default) return false;
    }
    
    // Default/Custom filter
    if (defaultFilter === 'default') {
      if (!category.is_default) return false;
    } else if (defaultFilter === 'custom') {
      if (category.is_default) return false;
    }
    
    return true;
  });

  const loadCategories = useCallback(async (showLoadingState = true) => {
    if (showLoadingState) setLoading(true);
    try {
      const response = await billCategoriesAPI.getAll(showInactive);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
      showError('Failed to load categories');
    } finally {
      if (showLoadingState) setLoading(false);
    }
  }, [showInactive, showError]);

  useEffect(() => {
    loadCategories(true);
  }, [loadCategories]);

  const handleAdd = () => {
    setSelectedCategory(null);
    setShowAddModal(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleToggleActive = async (category) => {
    try {
      await billCategoriesAPI.toggleActive(category.id, !category.is_active);
      success(`Category ${category.is_active ? 'deactivated' : 'activated'}`);
      loadCategories();
    } catch (error) {
      showError('Failed to update category status');
    }
  };

  const confirmDelete = async () => {
    if (!selectedCategory) return;

    try {
      await billCategoriesAPI.delete(selectedCategory.id);
      success('Category deleted successfully');
      setShowDeleteModal(false);
      setSelectedCategory(null);
      loadCategories();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedCategory) {
        await billCategoriesAPI.update(selectedCategory.id, data);
        success('Category updated successfully');
        setShowEditModal(false);
      } else {
        await billCategoriesAPI.create(data);
        success('Category created successfully');
        setShowAddModal(false);
      }
      setSelectedCategory(null);
      loadCategories();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to save category');
    }
  };

  // Filter config
  const filterConfig = [
    ...(isInHousehold && !isPrivateMode ? [{
      id: 'ownership',
      label: 'Ownership',
      type: 'select',
      value: categoryTypeFilter,
      onChange: setCategoryTypeFilter,
      options: [
        { value: 'all', label: 'All Categories' },
        { value: 'personal', label: 'Personal' },
        { value: 'household', label: 'Household' }
      ]
    }] : []),
    {
      id: 'type',
      label: 'Type',
      type: 'select',
      value: defaultFilter,
      onChange: setDefaultFilter,
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'default', label: 'Default' },
        { value: 'custom', label: 'Custom' }
      ]
    },
    ...(canEdit ? [{
      id: 'showInactive',
      label: 'Show Inactive',
      type: 'toggle',
      value: showInactive,
      onChange: setShowInactive
    }] : [])
  ];

  const activeFilterCount = [
    categoryTypeFilter !== 'all' ? 1 : 0,
    defaultFilter !== 'all' ? 1 : 0,
    showInactive ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className="household-section">
        <h2>Bill Categories</h2>
        <div className="loading-state">Loading categories...</div>
      </div>
    );
  }

  return (
    <div className="household-section">
      <div className="section-header">
        <div>
          <h2>Bill Categories</h2>
          <p className="section-description">
            {isPrivateMode 
              ? 'Create custom categories to organize your bills'
              : canEdit 
                ? 'Manage bill categories for your household' 
                : 'View bill categories'}
          </p>
        </div>
        {canEdit && (
          <div className="header-actions">
            <button className="btn btn-primary btn-icon-mobile" onClick={handleAdd} title="Add Category">
              <Plus size={18} />
              <span className="btn-text">Add Category</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      {filterConfig.length > 0 && (
        <div className="filters-bar">
          <FilterDropdown
            filters={filterConfig}
            activeCount={activeFilterCount}
          />
        </div>
      )}

      {filteredCategories.length === 0 ? (
        <div className="empty-state">
          <Tag size={48} />
          <h3>No Categories</h3>
          <p>{categories.length === 0 ? 'Create your first category to organize bills' : 'No categories match the selected filter'}</p>
        </div>
      ) : (
        <div className="categories-grid">
          {filteredCategories.map(category => (
            <CategoryCard
              key={category.id}
              category={category}
              canEdit={canEdit}
              isInHousehold={isInHousehold}
              isPrivateMode={isPrivateMode}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}

      <CategoryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleSave}
        isInHousehold={isInHousehold}
        isManager={isManager}
        isPrivateMode={isPrivateMode}
      />

      <CategoryModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedCategory(null); }}
        category={selectedCategory}
        onSubmit={handleSave}
        isInHousehold={isInHousehold}
        isManager={isManager}
        isPrivateMode={isPrivateMode}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedCategory(null); }}
        onConfirm={confirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedCategory?.name}"? This action cannot be undone. Bills using this category will need to be reassigned.`}
        confirmText="Delete Category"
        variant="danger"
      />
    </div>
  );
}

/**
 * Category Card Component
 */
function CategoryCard({ category, canEdit, isInHousehold, isPrivateMode, onEdit, onDelete, onToggleActive }) {
  const isHouseholdCategory = !!category.household_id;
  const isDefault = category.is_default;
  
  return (
    <div className={`category-card ${!category.is_active ? 'inactive' : ''} ${isDefault ? 'default-category' : ''}`}>
      <div className="category-card-header">
        <div className="category-emoji-badge">
          {category.icon || '📋'}
        </div>
        <div className="category-info">
          <h3>{category.name}</h3>
          {category.description && (
            <span className="category-description">{category.description}</span>
          )}
        </div>
        {!category.is_active && <span className="inactive-badge">Inactive</span>}
        {isDefault && <span className="default-badge">Default</span>}
        {!isDefault && isInHousehold && !isPrivateMode && (
          <OwnershipBadge 
            isHousehold={isHouseholdCategory} 
            variant="icon-only" 
            size="sm" 
          />
        )}
      </div>
      
      {!isDefault && category.created_by_name && (
        <div className="category-details">
          <div className="category-meta">
            <span className="meta-item">
              Created by {category.created_by_name}
            </span>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="category-actions">
          {/* Edit button - not shown for default categories */}
          {!isDefault && (
            <button className="btn-icon" onClick={() => onEdit(category)} title="Edit">
              <Edit2 size={16} />
              Edit
            </button>
          )}
          {/* Toggle active - shown for all categories */}
          <button className="btn-icon" onClick={() => onToggleActive(category)} title={category.is_active ? 'Deactivate' : 'Activate'}>
            {category.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            {category.is_active ? 'Deactivate' : 'Activate'}
          </button>
          {/* Delete button - not shown for default categories */}
          {!isDefault && (
            <button className="btn-icon danger" onClick={() => onDelete(category)} title="Delete">
              <Trash2 size={16} />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Category Modal Component
 */
function CategoryModal({ isOpen, onClose, category, onSubmit, isInHousehold, isManager, isPrivateMode }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '📋',
    isHouseholdCategory: false
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        icon: category.icon || '📋',
        isHouseholdCategory: !!category.household_id
      });
    } else {
      setFormData({
        name: '',
        description: '',
        icon: '📋',
        isHouseholdCategory: isInHousehold && isManager && !isPrivateMode
      });
    }
    setError('');
  }, [category, isOpen, isInHousehold, isManager, isPrivateMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    setSaving(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button type="submit" form="category-form" className="btn btn-primary" disabled={saving}>
        {saving ? 'Saving...' : category ? 'Update Category' : 'Create Category'}
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={category ? 'Edit Category' : 'Add Category'}
      icon={<Tag size={24} />}
      footer={footer}
      disabled={saving}
    >
      <form id="category-form" onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}

          {/* Personal vs Household Toggle */}
          {isInHousehold && isManager && !isPrivateMode && !category && (
            <FormGroup label="Category Ownership">
              <div className="ownership-toggle">
                <button
                  type="button"
                  className={`ownership-option ${!formData.isHouseholdCategory ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, isHouseholdCategory: false })}
                  disabled={saving}
                >
                  <User size={18} />
                  <div>
                    <span className="ownership-label">Personal</span>
                    <span className="ownership-desc">Only you can see this category</span>
                  </div>
                </button>
                <button
                  type="button"
                  className={`ownership-option ${formData.isHouseholdCategory ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, isHouseholdCategory: true })}
                  disabled={saving}
                >
                  <Home size={18} />
                  <div>
                    <span className="ownership-label">Household</span>
                    <span className="ownership-desc">Shared with all members</span>
                  </div>
                </button>
              </div>
            </FormGroup>
          )}

          <FormGroup label="Category Name" required htmlFor="name">
            <TextInput
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={saving}
              placeholder="e.g., Utilities, Insurance, Subscriptions"
            />
          </FormGroup>

          <FormGroup label="Description" htmlFor="description">
            <TextInput
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={saving}
              placeholder="Optional description for this category"
            />
          </FormGroup>

          <FormGroup label="Icon">
            <div className="emoji-picker-grid">
              {CATEGORY_EMOJIS.map(emoji => (
                <button
                  key={emoji.value}
                  type="button"
                  className={`emoji-option ${formData.icon === emoji.value ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, icon: emoji.value }))}
                  title={emoji.label}
                  disabled={saving}
                >
                  {emoji.value}
                </button>
              ))}
            </div>
          </FormGroup>
        </div>
      </form>
    </BaseModal>
  );
}

export default BillCategories;
